import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { BaseEmployeeDTOSalutationEnum, UpdateEmployeeDTO, UpdateUserDTO } from 'probonio-shared-ui/api';
import { apis } from 'probonio-shared-ui/module/api';
import { loadMe } from 'probonio-shared-ui/module/me';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BasicDialog, DialogState } from '../../component/dialog';
import { DialogLoadingButton } from '../../component/dialog/DialogLoadingButton';
import i18n from '../../lang';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import UserEditForm, { MeFields } from './UserEditForm';

type MeUpdateFields = Pick<UpdateEmployeeDTO, 'firstName' | 'lastName' | 'title'> &
  Pick<UpdateUserDTO, 'localeManager'> & {
    // UpdateEmployeeDTOSalutationEnum's generated type is missing `| null` (OpenAPI Generator core 6.0.1 drops
    // `nullable` for enum properties); the backend accepts/persists null fine.
    salutation?: UpdateEmployeeDTO['salutation'] | null;
  };

interface Props {
  dialogState: DialogState;
}

export const UserEditDialog: React.FC<Props> = ({ dialogState }) => {
  const { t } = useTranslation('navigationModule');
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { user: currentUser, ...me } = useAppSelector(state => state.me);
  const currentEmployment = currentUser?.employments?.find(employment => employment.tenantId === me.currentTenantId);

  const { handleSubmit, control, getValues, reset } = useForm<MeFields>();

  useEffect(() => {
    if (!dialogState.isOpen) {
      return;
    }
    reset({
      firstName: currentEmployment?.firstName ?? '',
      lastName: currentEmployment?.lastName ?? '',
      title: currentEmployment?.title ?? '',
      salutation: (currentEmployment?.salutation as BaseEmployeeDTOSalutationEnum) ?? null,
      locale: currentUser?.localeManager ?? i18n.resolvedLanguage,
    });
  }, [currentEmployment, currentUser, dialogState.isOpen, reset]);

  const mutation = useMutation({
    mutationFn: async ({ firstName, lastName, title, salutation, ...userDTO }: MeUpdateFields) => {
      if (currentEmployment && me.currentTenantId) {
        await apis.employee.updateEmployee({
          employeeId: currentEmployment.id,
          tenantId: me.currentTenantId,
          // TODO PRO-6690: generated UpdateEmployeeDTOSalutationEnum type is missing `| null`; backend accepts/persists null fine.
          updateEmployeeDTO: { firstName, lastName, salutation: salutation as UpdateEmployeeDTO['salutation'], title },
        });
      }
      return apis.user.updateUser({ userId: currentUser!.id, updateUserDTO: userDTO });
    },
    onSuccess: async () => {
      await dispatch(loadMe());
      enqueueSnackbar(t('userEdit.success'), { variant: 'success' });
    },
    onError: async () => {
      enqueueSnackbar(t('userEdit.error'), { variant: 'error' });
    },
  });

  const handleSubmitMe = useCallback(() => {
    const updateMe: MeUpdateFields = {
      firstName: getValues('firstName'),
      lastName: getValues('lastName'),
      title: getValues('title'),
      salutation: getValues('salutation') || null,
      localeManager: getValues('locale'),
    };
    mutation.mutate(updateMe);
    if (i18n.resolvedLanguage !== updateMe.localeManager) {
      void i18n.changeLanguage(updateMe.localeManager);
    }
    dialogState.handleClose();
  }, [dialogState, getValues, mutation]);

  return (
    <BasicDialog
      dialogState={dialogState}
      maxWidth="xs"
      fullWidth
      title={t('userEdit.dialogTitle')}
      content={<UserEditForm control={control} onSubmit={handleSubmit(handleSubmitMe)} />}
      actions={
        <DialogLoadingButton loading={mutation.isPending} size="small" onClick={handleSubmit(handleSubmitMe)}>
          {t('common:buttons.save')}
        </DialogLoadingButton>
      }
    />
  );
};
